import React from "react";
import RegisterPage, { STATUS_BADGES, PRIORITY_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { apiFetch } from "../lib/api";
import { Button } from "../components/ui/button";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { useAuth } from "../lib/auth";

const CLASSIF = {
  FAC: { bg: "#FEF3C7", color: "#854D0E" },
  MTC: { bg: "#FFE4E6", color: "#9F1239" },
  RWC: { bg: "#FEE2E2", color: "#7F1D1D" },
  LTI: { bg: "#FEE2E2", color: "#7F1D1D" },
  "Near Miss": { bg: "#DBEAFE", color: "#1E40AF" },
  "Property Damage": { bg: "#F1F5F9", color: "#334155" },
  Environmental: { bg: "#DCFCE7", color: "#166534" },
  Fire: { bg: "#FFE4E6", color: "#9F1239" },
  Spill: { bg: "#DBEAFE", color: "#1E40AF" },
};

export default function Incidents() {
  const { data, siteNames } = useHSE();
  const { hasPermission } = useAuth();
  const rows = data.incidents || [];

  const exportInvestigationReport = () => {
    const rec = rows[0];
    if (!rec) { toast.error("No incident to report"); return; }
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const w = doc.internal.pageSize.getWidth();
    doc.setFillColor(8, 43, 77);
    doc.rect(0, 0, w, 70, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16); doc.text("Incident Investigation Report", 30, 32);
    doc.setFontSize(10); doc.setFont("helvetica", "normal");
    doc.text(`Corporate ISO 45001 · ${new Date().toLocaleString()}`, 30, 52);
    doc.setTextColor(30, 30, 30); let y = 100;
    const addRow = (k, v) => {
      doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.text(k, 30, y);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      const lines = doc.splitTextToSize(String(v ?? "—"), w - 200);
      doc.text(lines, 200, y);
      y += Math.max(16, lines.length * 12 + 4);
    };
    addRow("Incident ID", rec.id);
    addRow("Date / Time", rec.dateTime || rec.date);
    addRow("Site / Area / Location", `${rec.site} / ${rec.area} / ${rec.location}`);
    addRow("Contractor", rec.contractor);
    addRow("Involved Person", `${rec.personName} (${rec.jobTitle})`);
    addRow("Classification", rec.classification);
    addRow("Actual Severity", rec.severity);
    addRow("Potential Severity", rec.potentialSeverity);
    addRow("Description", rec.description);
    addRow("Immediate Action", rec.immediateAction);
    addRow("Root Cause", rec.rootCause);
    addRow("RCA Method", rec.rcaMethod);
    addRow("Corrective Action", rec.correctiveAction);
    addRow("Preventive Action", rec.preventiveAction);
    addRow("Linked Action ID", rec.linkedActionId);
    addRow("Status", rec.status);
    doc.setFontSize(8); doc.setTextColor(120, 120, 120);
    doc.text(`Generated by HSE Command Center — Confidential`, 30, doc.internal.pageSize.getHeight() - 20);
    doc.save(`incident-report-${rec.id}.pdf`);
    toast.success("Investigation report exported");
  };

  const Top = (
    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
      <StatCard label="Total" value={rows.length} testid="inc-total" />
      <StatCard label="FAC" value={rows.filter((r) => r.classification === "FAC").length} tone="warning" />
      <StatCard label="MTC" value={rows.filter((r) => r.classification === "MTC").length} tone="warning" />
      <StatCard label="LTI" value={rows.filter((r) => r.classification === "LTI").length} tone="critical" />
      <StatCard label="Near Miss" value={rows.filter((r) => r.classification === "Near Miss").length} tone="info" />
      <StatCard label="Closed" value={rows.filter((r) => r.status === "Closed").length} tone="safe" />
    </div>
  );

  const handleBeforeSave = (editingRecord) => {
    if (!editingRecord.linkedActionId) {
      editingRecord.linkedActionId = `ACT-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`;
    }
    return null;
  };

  const handleSaveRecord = async (editingRecord) => {
    const classificationMap = { "Near Miss": "near_miss", "Property Damage": "property_damage", Environmental: "environmental" };
    const correctiveAction = editingRecord.correctiveAction || editingRecord.preventiveAction;
    try {
      const res = await apiFetch("/incidents", {
        method: "POST",
        body: {
          ...editingRecord,
          title: editingRecord.description || editingRecord.id || "Incident Record",
          incidentAt: editingRecord.dateTime || new Date().toISOString(),
          reportedAt: editingRecord.reportedDateTime || editingRecord.dateTime || new Date().toISOString(),
          locationName: editingRecord.location || editingRecord.area || "Site Area",
          classification: classificationMap[editingRecord.classification] || String(editingRecord.classification || "near_miss").toLowerCase(),
          injuryType: editingRecord.injuryType || undefined,
          bodyPart: editingRecord.bodyPart || undefined,
          isWorkRelated: editingRecord.isWorkRelated !== "No",
          isFatality: editingRecord.isFatality === "Yes",
          isOccupationalIllness: editingRecord.isOccupationalIllness === "Yes",
          isHipo: editingRecord.isHipo === "Yes" || editingRecord.potentialSeverity === "Critical",
          immediateCause: editingRecord.immediateCause,
          underlyingCause: editingRecord.underlyingCause,
          rootCause: editingRecord.rootCause,
          rcaTeam: String(editingRecord.rcaTeam || "").split(/\r?\n|,/).map((value) => value.trim()).filter(Boolean),
          witnesses: String(editingRecord.witnessStatements || "").split(/\r?\n/).map((statement) => ({ statement })).filter((item) => item.statement),
          regulatoryNotified: editingRecord.regulatoryNotified === "Yes",
          regulatoryNotifiedAt: editingRecord.regulatoryNotifiedAt || undefined,
          clientNotified: editingRecord.clientNotified === "Yes",
          clientNotifiedAt: editingRecord.clientNotifiedAt || undefined,
          lessonsLearned: editingRecord.lessonsLearned,
          restrictedMedical: hasPermission("incident.medical.edit") && editingRecord.restrictedMedicalNotes ? { notes: editingRecord.restrictedMedicalNotes } : undefined,
          actions: correctiveAction ? [{ title: correctiveAction, description: `${correctiveAction}${editingRecord.effectivenessVerification ? `\nEffectiveness verification plan: ${editingRecord.effectivenessVerification}` : ""}`, criticality: editingRecord.potentialSeverity === "Critical" ? "critical" : "high", dueDate: editingRecord.dueDate }] : [],
          idempotencyKey: `incident-${editingRecord.id}`,
        },
      });
      return res?.incident ? { ...editingRecord, ...res.incident } : editingRecord;
    } catch (err) {
      // If offline/development fallback
      return editingRecord;
    }
  };

  return (
    <RegisterPage
      title="Incident Register"
      subtitle="All recorded incidents — classification, RCA, corrective/preventive actions with Action Plan linkage"
      storeKey="incidents"
      idPrefix="INC"
      topPanels={Top}
      onBeforeSave={handleBeforeSave}
      onSaveRecord={handleSaveRecord}
      toolbarExtras={
        <Button data-testid="incident-report-btn" variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
          onClick={exportInvestigationReport}>
          <FileDown className="w-3.5 h-3.5" /> Investigation PDF
        </Button>
      }
      columns={[
        { key: "id", label: "Incident ID" },
        { key: "date", label: "Date" },
        { key: "site", label: "Site" },
        { key: "area", label: "Area" },
        { key: "contractor", label: "Contractor" },
        { key: "personName", label: "Person" },
        { key: "classification", label: "Class" },
        { key: "severity", label: "Actual Sev." },
        { key: "potentialSeverity", label: "Potential" },
        { key: "linkedActionId", label: "Linked Action", render: (r) => r.linkedActionId ? <span className="font-mono text-[11px] text-[#18B84F]">{r.linkedActionId}</span> : <span className="text-slate-300">—</span> },
        { key: "status", label: "Status" },
      ]}
      badgeMap={{ severity: PRIORITY_BADGES, potentialSeverity: PRIORITY_BADGES, classification: CLASSIF, status: STATUS_BADGES }}
      formFields={[
        { key: "id", label: "Incident ID" },
        { key: "dateTime", label: "Date & Time", type: "datetime-local" },
        { key: "reportedDateTime", label: "Reported Date & Time", type: "datetime-local" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "area", label: "Area" },
        { key: "location", label: "Exact Location" },
        { key: "department", label: "Department", type: "select", options: data.settings.departments },
        { key: "contractor", label: "Contractor / Company", type: "select", options: data.settings.contractors },
        { key: "personName", label: "Involved Person" },
        { key: "jobTitle", label: "Job Title / Trade" },
        { key: "injuryType", label: "Injury Type" },
        { key: "bodyPart", label: "Body Part" },
        { key: "type", label: "Incident Type", type: "select", options: data.settings.incidentTypes },
        { key: "classification", label: "Classification", type: "select", options: ["FAC", "MTC", "RWC", "LTI", "FAT", "ILL", "Near Miss", "Property Damage", "Environmental"] },
        { key: "isWorkRelated", label: "Work-related Classification", type: "select", options: ["Yes", "No"] },
        { key: "oshaRecordability", label: "OSHA Recordability (server-derived)", type: "select", options: ["Derived from classification"] },
        { key: "isFatality", label: "Fatality", type: "select", options: ["No", "Yes"] },
        { key: "isOccupationalIllness", label: "Occupational Illness", type: "select", options: ["No", "Yes"] },
        { key: "isHipo", label: "High-Potential Indicator", type: "select", options: ["No", "Yes"] },
        { key: "severity", label: "Actual Severity", type: "select", options: ["Low", "Medium", "High", "Critical"] },
        { key: "potentialSeverity", label: "Potential Severity", type: "select", options: ["Low", "Medium", "High", "Critical"] },
        { key: "description", label: "Description of Incident", type: "textarea", full: true },
        { key: "immediateAction", label: "Immediate Action Taken", type: "textarea", full: true },
        { key: "immediateCause", label: "Immediate Cause", type: "textarea", full: true },
        { key: "underlyingCause", label: "Underlying Cause", type: "textarea", full: true },
        { key: "rootCause", label: "Root Cause", type: "textarea", full: true },
        { key: "rcaMethod", label: "RCA Method", type: "select", options: ["5 Why", "Fishbone", "FMEA", "Tap Root"] },
        { key: "rcaTeam", label: "RCA Team (one per line)", type: "textarea", full: true },
        { key: "witnessStatements", label: "Witnesses / Statements (one per line)", type: "textarea", full: true },
        { key: "regulatoryNotified", label: "Regulatory Notification", type: "select", options: ["No", "Yes"] },
        { key: "regulatoryNotifiedAt", label: "Regulator Notified At", type: "datetime-local" },
        { key: "clientNotified", label: "Client Notification", type: "select", options: ["No", "Yes"] },
        { key: "clientNotifiedAt", label: "Client Notified At", type: "datetime-local" },
        { key: "correctiveAction", label: "Corrective Action", type: "textarea", full: true },
        { key: "preventiveAction", label: "Preventive Action", type: "textarea", full: true },
        { key: "actionOwner", label: "Action Owner" },
        { key: "dueDate", label: "Target Date", type: "date" },
        { key: "closureDate", label: "Closure Date", type: "date" },
        { key: "linkedActionId", label: "Linked Action Plan ID" },
        { key: "investigationApprovalNotes", label: "Investigation Approval Notes (approval remains governed by workflow)", type: "textarea", full: true },
        { key: "lessonsLearned", label: "Lessons Learned", type: "textarea", full: true },
        { key: "effectivenessVerification", label: "Effectiveness Verification Plan (completed on linked Action)", type: "textarea", full: true },
        ...(hasPermission("incident.medical.edit") ? [{ key: "restrictedMedicalNotes", label: "Restricted Medical Notes", type: "textarea", full: true }] : []),
      ]}
    />
  );
}
